import * as React from 'react';
import { cn } from '@/utils/cn';

interface BubbleData {
  id: number;
  style: React.CSSProperties;
  className: string;
  isGlowing: boolean;
}

interface BackgroundBubblesProps {
  count?: number;
  className?: string;
  variant?: 'default' | 'white';
}

/**
 * Decorative animated bubble background. `variant` switches between the
 * blue/purple default palette and a translucent-white palette for light
 * backgrounds. Pure presentation, no data deps.
 */
const BackgroundBubbles: React.FC<BackgroundBubblesProps> = ({
  count = 50,
  className,
  variant = 'default',
}) => {
  const bubbles = React.useMemo<BubbleData[]>(
    () =>
      Array.from({ length: count }, (_, i) => {
        const size = Math.random() * 14 + 4;
        const isGlowing = i % 8 === 0;
        const isFloating = i % 3 === 0;

        const animationDelay = `-${Math.random() * 20}s`;
        const duration = `${Math.random() * 10 + 10}s`;

        // Determine colors based on variant
        let colorClass = '';
        if (variant === 'white') {
          colorClass = i % 2 === 0 ? 'bg-white/40 blur-[1px]' : 'bg-white/20 blur-[1px]';
        } else {
          colorClass = i % 4 === 0 ? 'bg-blue-800/60 blur-[1px]' : 'bg-purple-700/40 blur-[1px]';
        }

        return {
          id: i,
          isGlowing,
          style: {
            width: `${size}px`,
            height: `${size}px`,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animationDelay: isFloating ? animationDelay : undefined,
            animationDuration: isFloating ? duration : undefined,
          },
          className: cn(
            'absolute rounded-full',
            colorClass,
            isFloating && 'animate-float',
            isGlowing && 'animate-glow'
          ),
        };
      }),
    [count, variant]
  );

  return (
    <div
      aria-hidden='true'
      className={cn(
        'pointer-events-none absolute inset-0 z-10 overflow-hidden',
        // Default blend mode if not overridden in className
        !className?.includes('mix-blend') && 'mix-blend-color-dodge',
        className
      )}
    >
      {bubbles.map((bubble) => (
        <div key={bubble.id} className={bubble.className} style={bubble.style}>
          {bubble.isGlowing && (
            <div
              className='absolute inset-0 size-full animate-flash rounded-full'
              style={{
                background:
                  variant === 'white'
                    ? 'radial-gradient(circle at center, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.1) 60%, transparent 80%)'
                    : 'radial-gradient(circle at center, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.4) 40%, transparent 70%)',
                animationDuration: bubble.style.animationDuration,
                animationDelay: bubble.style.animationDelay,
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default BackgroundBubbles;
